import format from 'date-fns/format'
import { FC } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import s from './AdSpotCard.module.scss'

import { AdSpot } from 'types/adspot'

import { getIconByMetaverse } from 'constants/MetaverseIcons'

import transformUTCToLocale from 'utils/transformUTCToLocale'

import { useDownloadFile } from 'hooks/useDownloadFile'
import { useModal } from 'hooks/useModal'
import { useShowDropdown } from 'hooks/useShowDropdown'

import { publisherDashboardApi } from 'api/publisherDashboard-api'

import { DeleteIcon } from 'assets/icons/DeleteIcon'
import { DownloadIcon } from 'assets/icons/DownloadIcon'
import { EditIcon } from 'assets/icons/EditIcon'
import { HeatmapIcon } from 'assets/icons/HeatmapIcon'
import { InfoIcon } from 'assets/icons/InfoIcon'

import { Card } from 'components/UI/Card/Card'
import ConfirmMessage from 'components/UI/ConfirmMessage'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { Dropmenu } from 'components/UI/Dropmenu/Dropmenu'
import { Status } from 'components/UI/Status/Status'

import { createAdSpotStatus } from 'pages/MyAdSpots/helpers/createAdSpotStatus'

interface IAdSpotCard {
  adSpot: AdSpot
  deleteAdSpot: (id: number) => void
}

export const STATUS_NAME_BY_STATUS_VALUE = {
  0: 'Viewed Recently',
  1: 'Online',
  2: 'Offline',
}

export const STATUS_STYLE_BY_STATUS_NUMBER = {
  0: '#3888ff',
  1: '#47d068',
  2: '#fb0943',
}

const AdSpotCard: FC<IAdSpotCard> = (props) => {
  const { adSpot, deleteAdSpot } = props
  const { ref, showDropdown } = useShowDropdown()

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [downloadReport] = useDownloadFile({
    getData: () =>  publisherDashboardApi.downloadReport(adSpot.id),

  })

  const { show, handleClose, handleOpen } = useModal()

  const openAdSpotInfoModal = () => {
    navigate(`${adSpot.id}/?${searchParams}`)
  }

  const editAdSpotHandler = () => {
    navigate(`edit/${adSpot.id}?${searchParams}`)
  }

  const date = format(transformUTCToLocale(adSpot.created_at), 'dd.MM.yyyy HH:mm')
  return (
    <>
      <Card variant="primary" className={s.cardWrapper} onClick={openAdSpotInfoModal}>
        <Card.Image height='14.063rem'>
          <img src={adSpot.preview_url} alt="Preview" />
          <img src={getIconByMetaverse(adSpot.spot_type)} alt="" className={s.metaLogo} />
          <Status
            statusClassName={s.status}
            label={STATUS_NAME_BY_STATUS_VALUE[createAdSpotStatus(adSpot.last_activity)]}
            color={STATUS_STYLE_BY_STATUS_NUMBER[createAdSpotStatus(adSpot.last_activity)]}
          />
        </Card.Image>
        <Card.Info className={s.cardInfo}>
          <div className={s.footer}>
            <Card.CroppedText>{adSpot.name}</Card.CroppedText>
            <p className={s.footerDate}>{date}</p>
          </div>
          <div className={s.buttonsWrapper}>
            {adSpot.has_scene && (
              <CustomButton
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`heatmap/${adSpot.id}`)
                }}
              >
                <HeatmapIcon className={s.heatmapIcon} />
              </CustomButton>
            )}
            <Dropmenu
              variant="primary"
              show={showDropdown}
              cref={ref}
              items={[
                { icon: <InfoIcon />, title: 'Show Info', onClick: openAdSpotInfoModal },
                { icon: <EditIcon />, title: 'Edit Parameters', onClick: editAdSpotHandler },
                { icon: <DownloadIcon />, title: 'Download Report', onClick: () => downloadReport() },
                { icon: <DeleteIcon />, title: 'Delete', onClick: handleOpen },
              ]}
            />
          </div>
        </Card.Info>
      </Card>
      <>
        <ConfirmMessage
          isOpen={show}
          handleClose={handleClose}
          message={`Are you sure you want to delete the "${adSpot.name}" Ad Space?`}
          deleteItem={deleteAdSpot}
          id={adSpot.id}
        />
      </>
    </>
  )
}

export default AdSpotCard
